<template>
  <v-container fluid class="d-flex justify-center" :style="`margin-left: ${margin}`">
    <div style="width: 100%; max-width: 1200px;">

      <!-- Header -->
      <div class="table-header-filters pa-4 mb-6">
        <v-row dense align="center">
          <v-col cols="12" sm="auto">
            <div class="period-pills">
              <span
                v-for="p in periods"
                :key="p.value"
                class="period-pill"
                :class="{ active: period === p.value }"
                @click="onPeriodClick(p.value)"
              >{{ p.label }}</span>
            </div>
          </v-col>

          <!-- Custom date inputs -->
          <v-col v-if="period === 'custom'" cols="12" sm="auto" class="d-flex align-center" style="gap: 6px;">
            <v-menu v-model="menuFrom" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-start</v-icon>
                  {{ formatDate(customFrom) }}
                </button>
              </template>
              <v-date-picker v-model="customFrom" no-title scrollable @input="menuFrom = false; fetchData()" />
            </v-menu>
            <span class="date-pill-sep">—</span>
            <v-menu v-model="menuTo" offset-y :close-on-content-click="false" min-width="auto">
              <template v-slot:activator="{ on, attrs }">
                <button class="period-pill date-pill" v-bind="attrs" v-on="on">
                  <v-icon x-small class="mr-1">mdi-calendar-end</v-icon>
                  {{ formatDate(customTo) }}
                </button>
              </template>
              <v-date-picker v-model="customTo" no-title scrollable @input="menuTo = false; fetchData()" />
            </v-menu>
          </v-col>

          <v-col cols="auto" class="ml-auto">
            <v-btn icon :to="{ name: `${targetModifier}.all-reports.reportMenu` }" class="close-btn">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-12">
        <v-progress-circular indeterminate color="cyan darken-1" size="48" />
        <div class="mt-4 grey--text">Učitavanje podataka...</div>
      </div>

      <template v-else>

        <!-- KPI ukupno -->
        <v-card outlined rounded="lg" class="pa-5 mb-6">
          <v-row align="center">
            <v-col>
              <div class="kpi-label">Ukupni promet</div>
              <div class="kpi-value">{{ formatEur(totalEur) }}</div>
            </v-col>
            <v-col class="text-right">
              <div class="kpi-label">Ukupno računa</div>
              <div class="kpi-value">{{ totalCount }}</div>
            </v-col>
          </v-row>
        </v-card>

        <!-- Shift kartice -->
        <v-row class="mb-6">
          <v-col
            v-for="shift in shifts"
            :key="shift.name"
            cols="12"
            sm="6"
          >
            <v-card outlined rounded="lg" class="pa-4" style="height: 100%;">
              <v-row dense align="center" class="mb-2">
                <v-col cols="auto">
                  <v-icon :color="shift.color" size="22">{{ shift.icon }}</v-icon>
                </v-col>
                <v-col>
                  <span class="shift-name">{{ shift.name }}</span>
                  <span class="shift-range ml-2">{{ shiftRange(shift) }}</span>
                </v-col>
              </v-row>
              <div class="shift-eur">{{ formatEur(shift.totalEur) }}</div>
              <div class="shift-count">{{ shift.count }} računa</div>
              <div class="shift-bar-track">
                <div
                  class="shift-bar-fill"
                  :style="{ width: barWidth(shift) + '%', background: shift.color }"
                />
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Bar chart -->
        <v-card outlined rounded="lg" class="pa-6">
          <div class="section-title mb-4">Promet po smjenama</div>
          <apexchart
            type="bar"
            height="260"
            :options="chartOptions"
            :series="chartSeries"
          />
        </v-card>

      </template>
    </div>
  </v-container>
</template>

<script>
import { getFirestore, collection, query, where, getAggregateFromServer, sum, count } from 'firebase/firestore'
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import VueApexCharts from 'vue-apexcharts'
import Vue from 'vue'

Vue.use(VueApexCharts)
Vue.component('apexchart', VueApexCharts)

const SHIFTS = [
  { name: 'Jutro', icon: 'mdi-weather-sunrise',  color: '#FF9800', start: 6,  end: 12 },
  { name: 'Podne', icon: 'mdi-weather-sunny',    color: '#00BCD4', start: 12, end: 17 },
  { name: 'Večer', icon: 'mdi-weather-sunset',   color: '#3989C9', start: 17, end: 24 },
  { name: 'Noć',   icon: 'mdi-weather-night',   color: '#6154A4', start: 0,  end: 6  },
]

export default {
  name: 'SmjeneProhoda',
  inject: ['showMsgBox', 'showLoader', 'hideLoader'],

  data () {
    const today = new Date().toISOString().substr(0, 10)
    return {
      margin: '0px',
      targetModifier: '',
      loading: false,
      period: 'today',
      periods: [
        { value: 'today',     label: 'Danas'       },
        { value: 'yesterday', label: 'Jučer'       },
        { value: 'week',      label: 'Tjedan'      },
        { value: 'custom',    label: 'Prilagođeno' },
      ],
      menuFrom: false,
      menuTo: false,
      customFrom: today,
      customTo: today,
      shifts: [],
    }
  },

  computed: {
    maxShiftEur () {
      return Math.max(...this.shifts.map(s => s.totalEur), 1)
    },
    totalEur () {
      return this.shifts.reduce((a, s) => a + s.totalEur, 0)
    },
    totalCount () {
      return this.shifts.reduce((a, s) => a + s.count, 0)
    },
    chartSeries () {
      return [{
        name: 'Promet (EUR)',
        data: this.shifts.map(s => parseFloat(s.totalEur.toFixed(2))),
      }]
    },
    chartOptions () {
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Raleway, sans-serif' },
        plotOptions: { bar: { horizontal: true, barHeight: '60%', distributed: true } },
        colors: SHIFTS.map(s => s.color),
        xaxis: {
          categories: this.shifts.map(s => s.name),
          labels: { style: { fontSize: '12px' } },
        },
        yaxis: {
          labels: { style: { fontSize: '13px', fontWeight: 600 } },
        },
        dataLabels: {
          enabled: true,
          formatter: v => this.formatEur(v),
          style: { fontSize: '12px', fontWeight: 600 },
        },
        legend: { show: false },
        tooltip: {
          y: { formatter: v => this.formatEur(v) },
        },
        grid: { borderColor: '#E0E6ED', strokeDashArray: 3 },
      }
    },
  },

  async created () {
    this.margin = state.getMargin()
    const sideNav = this.$route.meta?.sideNav
    if (sideNav === 'Company') this.targetModifier = 'company'
    else if (sideNav === 'PointOfSale') this.targetModifier = 'pointOfSale'
    EventBus.$on('set-mini', () => { this.margin = state.getMargin() })
    await this.fetchData()
  },

  beforeDestroy () {
    EventBus.$off('set-mini')
  },

  methods: {
    formatEur (v) {
      return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v)
    },

    formatDate (d) {
      if (!d) return ''
      const [y, m, day] = d.split('-')
      return `${day}.${m}.${y}`
    },

    onPeriodClick (value) {
      this.period = value
      if (value !== 'custom') this.fetchData()
    },

    shiftRange (shift) {
      const pad = n => String(n).padStart(2, '0')
      const endHour = shift.end === 24 ? 0 : shift.end
      return `${pad(shift.start)}:00 – ${pad(endHour)}:00`
    },

    barWidth (shift) {
      return (shift.totalEur / this.maxShiftEur) * 100
    },

    getDateRange () {
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      if (this.period === 'today') return { from: today, to: today }
      if (this.period === 'yesterday') {
        const y = new Date(today)
        y.setDate(y.getDate() - 1)
        return { from: y, to: y }
      }
      if (this.period === 'custom' && this.customFrom && this.customTo) {
        return { from: new Date(this.customFrom), to: new Date(this.customTo) }
      }
      // week: Mon – today
      const day = today.getDay() || 7
      const mon = new Date(today)
      mon.setDate(today.getDate() - day + 1)
      return { from: mon, to: today }
    },

    async fetchData () {
      this.loading = true
      try {
        const { from, to } = this.getDateRange()

        const days = []
        const d = new Date(from)
        while (d <= to) {
          days.push(new Date(d))
          d.setDate(d.getDate() + 1)
        }

        const db = getFirestore()
        const companyId = state.getCurrentCompany().id
        const col = collection(db, `receipts/${companyId}/receipts`)

        this.shifts = await Promise.all(SHIFTS.map(async shift => {
          const snaps = await Promise.all(days.map(day => {
            const start = Math.floor(
              new Date(day.getFullYear(), day.getMonth(), day.getDate(), shift.start).getTime() / 1000
            )
            const end = shift.end === 24
              ? Math.floor(new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1).getTime() / 1000)
              : Math.floor(new Date(day.getFullYear(), day.getMonth(), day.getDate(), shift.end).getTime() / 1000)
            return getAggregateFromServer(
              query(col,
                where('issue_date_time', '>=', start),
                where('issue_date_time', '<', end)
              ),
              { total: sum('total'), cnt: count() }
            )
          }))
          const totalCents = snaps.reduce((acc, s) => acc + (s.data().total || 0), 0)
          const totalCount = snaps.reduce((acc, s) => acc + (s.data().cnt || 0), 0)
          return { ...shift, totalEur: totalCents / 100, count: totalCount }
        }))
      } catch (err) {
        console.error('SmjeneProhoda:', err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.close-btn {
  width: 36px !important;
  height: 36px !important;
  min-width: 36px !important;
  border-radius: 10px !important;
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(229, 57, 53, 0.2) !important;
  color: #e53935 !important;
  transition: all 0.2s ease !important;
  box-shadow: none !important;
}
.close-btn:hover {
  background: rgba(229, 57, 53, 0.07) !important;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1) !important;
}
.table-header-filters {
  position: sticky;
  top: 64px;
  z-index: 10;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.period-pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.period-pill {
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  color: #6B7280;
  cursor: pointer;
  border: 1.5px solid #E2E8F0;
  background: #fff;
  transition: all 0.15s;
  white-space: nowrap;
}
.period-pill:hover {
  border-color: #00BCD4;
  color: #00838F;
  background: rgba(0, 188, 212, 0.05);
}
.period-pill.active {
  border-color: rgba(0, 188, 212, 0.5);
  background: rgba(0, 188, 212, 0.1);
  color: #00838F;
  font-weight: 600;
}
.date-pill {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.3px;
}
.date-pill-sep {
  font-size: 13px;
  color: #9CA3AF;
  font-weight: 500;
}
.kpi-label {
  font-size: 12px;
  color: #9CA3AF;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}
.kpi-value {
  font-size: 24px;
  font-weight: 700;
  color: #2D3748;
}
.shift-name {
  font-size: 15px;
  font-weight: 700;
  color: #2D3748;
}
.shift-range {
  font-size: 12px;
  color: #9CA3AF;
  font-family: monospace;
}
.shift-eur {
  font-size: 22px;
  font-weight: 700;
  color: #2D3748;
  margin-top: 4px;
}
.shift-count {
  font-size: 12px;
  color: #9CA3AF;
  margin-bottom: 4px;
}
.shift-bar-track {
  height: 8px;
  border-radius: 4px;
  background: #F1F5F9;
  margin-top: 8px;
  overflow: hidden;
}
.shift-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.6s ease;
}
.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #2D3748;
}
</style>
